'use client';
import React, { createContext, useContext, useState, useCallback } from 'react';

export type UserRole = 'Admin' | 'User' | 'Viewer';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarInitials: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string, role: UserRole) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// BACKEND INTEGRATION POINT: Replace mock auth logic with Supabase Auth calls
const MOCK_USERS: (AuthUser & { password: string })[] = [
  { id: 'user-001', name: 'Alexandra Mercer', email: 'admin@propertytracker.com', password: 'Admin123!', role: 'Admin', avatarInitials: 'AM' },
  { id: 'user-002', name: 'Derek Osei', email: 'user@propertytracker.com', password: 'User123!', role: 'User', avatarInitials: 'DO' },
  { id: 'user-003', name: 'Vivian Torres', email: 'viewer@propertytracker.com', password: 'Viewer123!', role: 'Viewer', avatarInitials: 'VT' },
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);

  const login = useCallback(async (email: string, password: string) => {
    // BACKEND INTEGRATION POINT: await supabase.auth.signInWithPassword({ email, password })
    const found = MOCK_USERS.find(u => u.email === email && u.password === password);
    if (found) {
      const { password: _p, ...authUser } = found;
      setUser(authUser);
      return { success: true };
    }
    return { success: false, error: 'Invalid email or password.' };
  }, []);

  const signup = useCallback(async (name: string, email: string, _password: string, role: UserRole) => {
    // BACKEND INTEGRATION POINT: await supabase.auth.signUp({ email, password }) + insert profile row
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const newUser: AuthUser = { id: `user-${Date.now()}`, name, email, role, avatarInitials: initials };
    setUser(newUser);
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    // BACKEND INTEGRATION POINT: await supabase.auth.signOut()
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}